<script setup lang="ts">
import { computed } from 'vue'
import type { TourStep } from '../types/tour.types'
import { renderTourMarkdown } from '../composables/tour-markdown'

const props = defineProps<{
  step: TourStep
  stepIndex: number
  totalSteps: number
  isFirst: boolean
  isLast: boolean
  tourTitle: string
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'prev'): void
  (e: 'skip'): void
  (e: 'close'): void
  (e: 'execute-ai', prompt: string): void
  (e: 'disable-training'): void
}>()

const bodyHtml = computed(() => renderTourMarkdown(props.step.body))
</script>

<template>
  <div class="tour-sheet" role="dialog" :aria-label="step.title">
    <div class="tour-sheet__grip" aria-hidden="true" />
    <header class="tour-sheet__head">
      <span class="tour-sheet__eyebrow">{{ tourTitle }} · Step {{ stepIndex + 1 }} of {{ totalSteps }}</span>
      <button type="button" class="tour-sheet__close" aria-label="Close tour" @click="emit('close')">
        <i class="fa-solid fa-xmark" />
      </button>
    </header>

    <h3 class="tour-sheet__title">{{ step.title }}</h3>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div class="tour-sheet__body" v-html="bodyHtml" />

    <button
      v-if="step.aiAction"
      type="button"
      class="tour-sheet__ai"
      @click="emit('execute-ai', step.aiAction.prompt)"
    >
      <i class="fa-solid fa-wand-magic-sparkles" />
      {{ step.aiAction.label ?? 'Execute with AI' }}
    </button>

    <div class="tour-sheet__progress" aria-hidden="true">
      <span
        v-for="n in totalSteps"
        :key="n"
        class="tour-sheet__seg"
        :class="{ 'tour-sheet__seg--on': n <= stepIndex + 1 }"
      />
    </div>

    <footer class="tour-sheet__foot">
      <button v-if="!isFirst" type="button" class="tour-sheet__btn tour-sheet__btn--ghost" @click="emit('prev')">
        <i class="fa-solid fa-arrow-left" /> Back
      </button>
      <button v-if="!isLast" type="button" class="tour-sheet__btn tour-sheet__btn--text" @click="emit('skip')">
        Skip
      </button>
      <button type="button" class="tour-sheet__btn tour-sheet__btn--primary" @click="emit('next')">
        {{ isLast ? 'Finish' : 'Next' }}
        <i :class="isLast ? 'fa-solid fa-check' : 'fa-solid fa-arrow-right'" />
      </button>
    </footer>

    <!-- Opt out of the guided tours on every screen (the help button can turn them back on). -->
    <button
      type="button"
      class="tour-sheet__optout"
      data-testid="tour-disable-training"
      @click="emit('disable-training')"
    >
      <i class="fa-solid fa-toggle-off" /> Turn off training mode
    </button>
  </div>
</template>

<style scoped lang="scss">
.tour-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3210;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 30px rgba(17, 29, 43, 0.22);
  padding: 10px 20px calc(18px + env(safe-area-inset-bottom));
  animation: tour-sheet-up 300ms cubic-bezier(0.16, 1, 0.3, 1) both;

  &__grip {
    width: 40px;
    height: 4px;
    border-radius: 999px;
    background: rgba(var(--v-theme-on-surface), 0.18);
    margin: 0 auto 12px;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  &__eyebrow {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgb(var(--v-theme-primary));
  }

  &__close {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), 0.55);
    cursor: pointer;
  }

  &__title {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 4px;
  }

  &__body {
    font-size: 0.9rem;
    line-height: 1.5;
    color: rgba(var(--v-theme-on-surface), 0.78);
    max-height: 30vh;
    overflow-y: auto;

    :deep(ul) {
      margin: 4px 0;
      padding-left: 18px;
    }
  }

  &__ai {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    padding: 9px 16px;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(120deg, #46c8de, #892a54);
    cursor: pointer;
  }

  &__progress {
    display: flex;
    gap: 4px;
    margin: 14px 0 12px;
  }

  &__seg {
    flex: 1;
    height: 4px;
    border-radius: 999px;
    background: rgba(var(--v-theme-on-surface), 0.12);

    &--on {
      background: rgb(var(--v-theme-primary));
    }
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__optout {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    margin-top: 12px;
    padding: 6px 0;
    border: 0;
    background: none;
    font-size: 0.8rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 10px;
    padding: 11px 16px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;

    &--primary {
      color: #fff;
      background: rgb(var(--v-theme-primary));
      margin-left: auto;
    }
    &--ghost {
      color: rgba(var(--v-theme-on-surface), 0.7);
      background: transparent;
      border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
    &--text {
      color: rgba(var(--v-theme-on-surface), 0.5);
      background: transparent;
    }
  }
}

@keyframes tour-sheet-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tour-sheet {
    animation: none;
  }
}
</style>
